import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Play, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { AGENT_TOOLS } from '@/config/agent-tools';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function TestingSandbox() {
  const [selectedTool, setSelectedTool] = useState<string>('');
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const toolEntries = Object.entries(AGENT_TOOLS);

  const executeTool = async () => {
    if (!selectedTool || !testInput) {
      toast.error('Please select a tool and provide input');
      return;
    }

    setIsExecuting(true);
    setTestResult(null);

    try {
      let inputData;
      try {
        inputData = JSON.parse(testInput);
      } catch {
        toast.error('Invalid JSON input');
        setIsExecuting(false);
        return;
      }

      // Map tool name to edge function
      const functionMap: Record<string, string> = {
        routeCommand: 'bridgette-command-router',
        createJob: 'bridgette-create-job',
        createLead: 'bridgette-create-lead',
        generateEstimate: 'bridgette-generate-estimate',
        getMeasurement: 'bridgette-get-measurements',
        scheduleAppointment: 'bridgette-schedule-appointment',
        sendFollowUp: 'bridgette-send-followup',
        getDailyBriefing: 'bridgette-get-daily-briefing',
      };

      const functionName = functionMap[selectedTool];
      if (!functionName) {
        toast.error(`No edge function mapped for tool: ${selectedTool}`);
        setIsExecuting(false);
        return;
      }

      const startTime = Date.now();
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: inputData,
      });
      const endTime = Date.now();

      setTestResult({
        success: !error,
        data: data || null,
        error: error || null,
        executionTime: endTime - startTime,
        timestamp: new Date().toISOString(),
      });

      if (error) {
        toast.error(`Tool execution failed: ${error.message}`);
      } else {
        toast.success('Tool executed successfully');
      }
    } catch (error: any) {
      console.error('Tool execution error:', error);
      setTestResult({
        success: false,
        error: error.message || String(error),
        timestamp: new Date().toISOString(),
      });
      toast.error('Tool execution failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const getExampleInput = (toolName: string) => {
    const examples: Record<string, any> = {
      createJob: {
        owner_name: 'John Doe',
        property_address: '123 Main St, City, State 12345',
        owner_phone: '555-0123',
        owner_email: 'john@example.com',
        notes: 'Initial consultation requested',
      },
      createLead: {
        contact_name: 'Jane Smith',
        property_address: '456 Oak Ave, City, State 12345',
        contact_phone: '555-0456',
        contact_email: 'jane@example.com',
        notes: 'Interested in roof replacement',
      },
      getDailyBriefing: {},
      getMeasurement: {
        address: '123 Main St, City, State 12345',
      },
    };

    return examples[toolName] || {};
  };

  const loadExample = () => {
    if (selectedTool) {
      const example = getExampleInput(selectedTool);
      setTestInput(JSON.stringify(example, null, 2));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Function Testing Sandbox</CardTitle>
          <CardDescription>Test agent tools directly with custom inputs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Tool</label>
            <Select value={selectedTool} onValueChange={setSelectedTool}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a tool to test" />
              </SelectTrigger>
              <SelectContent>
                {toolEntries.map(([key, tool]) => (
                  <SelectItem key={key} value={key}>
                    {tool.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTool && (
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-sm">
                    {AGENT_TOOLS[selectedTool as keyof typeof AGENT_TOOLS].name}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {AGENT_TOOLS[selectedTool as keyof typeof AGENT_TOOLS].description}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={loadExample}>
                  Load Example
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Input Parameters (JSON)</label>
            <Textarea
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder='{"key": "value"}'
              className="font-mono text-sm h-64"
            />
          </div>

          <Button
            onClick={executeTool}
            disabled={isExecuting || !selectedTool || !testInput}
            className="w-full"
          >
            {isExecuting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Executing...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Execute Tool
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Execution Result</CardTitle>
          <CardDescription>Output from tool execution</CardDescription>
        </CardHeader>
        <CardContent>
          {!testResult ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p className="text-sm">Execute a tool to see results here</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <Badge className="bg-green-500/10 text-green-500">Success</Badge>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-500" />
                    <Badge className="bg-red-500/10 text-red-500">Failed</Badge>
                  </>
                )}
                {testResult.executionTime && (
                  <Badge variant="outline">{testResult.executionTime}ms</Badge>
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                Executed at: {new Date(testResult.timestamp).toLocaleString()}
              </div>

              <ScrollArea className="h-96">
                <div className="bg-muted rounded-lg p-4">
                  <pre className="text-xs whitespace-pre-wrap font-mono">
                    {JSON.stringify(testResult.data || testResult.error, null, 2)}
                  </pre>
                </div>
              </ScrollArea>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
